<template>
    <div class="card">
        <DataTable
            v-model:first="offset"
            :rows="numberOfRows"
            :paginator="true"
            :lazy="true"
            @page="onPage"
            :totalRecords="totalFormulas"

            :value="formulas"
            :resizableColumns="true"
            ref="dt"
            dataKey="id"
            :rowHover="true"
            :loading="state.loading"
            responsiveLayout="scroll"

            :rowsPerPageOptions="[5, 10, 20, 50]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #header>
                <div class="flex justify-content-end flex-column sm:flex-row">
                    <div>
                        <span v-if="isGodUser">
                            <label for="filter">Filter By:</label>
                            <Dropdown class="ml-2" v-model="state.shared" :options="getSharedTypes()" optionLabel="name" optionValue="name" placeholder="Select Option" />

                            <Button type="button" icon="pi pi-filter" label="Filter" class="p-button-outlined p-button-success ml-2" @click="applyFilter" />
                            <Button type="button" icon="pi pi-filter-slash" label="Clear" class="p-button-outlined p-button-danger ml-2" @click="clearFilter" />
                        </span>
                    </div>
                </div>
            </template>
            <template #empty> No formulas available. </template>
            <template #loading> Loading Formulas. Please wait. </template>
            <Column field="id" header="ID" style="width: 4rem" />
            <Column field="code" header="CODE" style="width: 12rem" />
            <Column field="description" header="DESCRIPTION" />

            <Column field="type" header="TYPE" style="width: 8rem">
                <template #body="slotProps">
                    {{ getFormulaType(slotProps.data.type) }}
                </template>
            </Column>

            <Column field="created_at" header="CREATED AT" style="width: 6rem">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.created_at) }}
                </template>
            </Column>

            <Column field="actions" header="ACTIONS" style="width: 10rem">
                <template #body="slotProps">
                    <Button icon="pi pi-eye" class="p-button-rounded p-button-info mr-2" v-tooltip="'View Formula Details'" @click="operateFormula(slotProps.data.id, 'view')" />
                    <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" v-tooltip="'Edit Formula'" @click="operateFormula(slotProps.data.id, 'edit')" />
                    <Button icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2" v-tooltip="'Delete Formula'" @click="confirmDelete(slotProps.data.id)" />
                </template>
            </Column>
        </DataTable>
    </div>
    <!-- Formula Details Component goes here -->

    <Dialog v-model:visible="viewDetailsDialog" :style="{ width: '450px' }" header="Formula Details" :modal="true" :dismissableMask="true">
        <formula-details v-if="formula && formula?.id" />
    </Dialog>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';

import FormulaDetails from './FormulaDetails.vue';
import useUserComposable from '@/composables/UserComposable';
import useFormulaComposable from '@/composables/FormulaComposable';

import { formatDate } from '@/helpers/date-formatter';
import { getFormulaType, getSharedTypes } from '@/helpers/enum-helpers';
import {
    buildPagination,
    getFilters,
    getPage,
    getPerPage,
    setFilters,
    setPage, setPerPage,
} from '@/composables/PaginationComposable';

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const { isGodUser } = useUserComposable();
const { formula, formulas, fetchFormulas, fetchFormula, deleteFormula, totalFormulas } = useFormulaComposable();

const viewDetailsDialog = ref(false);
const createInitialState = () => {
    return reactive({
        loading: false,
        shared: 'Company',
    });
};
const state = createInitialState();

const pageList = 'formulaList';

onMounted(async () => {
    buildPagination(pageList, 0, 10, [{field: "id", order: -1}])

    const filters = getFilters(pageList)
    if (typeof filters !== 'undefined' && typeof filters.shared !== 'undefined') {
        state.shared = filters.shared
    }

    applyFilter()
});

const offset = computed(() => {
    console.log('offset::computed', getPage(pageList), getPerPage(pageList))
    return getPage(pageList) * getPerPage(pageList)
})

const numberOfRows = computed(() => {
    return getPerPage(pageList)
})

const onPage = async (event) => {
    state.lazyParams = event;
    setPage(pageList, event.page)
    setPerPage(pageList, event.rows)

    applyFilter()
};

const reloadTable = (newValue = null) => {
    state.loading = true;
    fetchFormulas(newValue, true, pageList).finally(() => {
        state.loading = false;
    });
};

const applyFilter = () => {
    setFilters(pageList, {
        shared: state.shared,
    })

    reloadTable(state.shared)
};

const clearFilter = async () => {
    state.shared = 'Company';

    setFilters(pageList, {
        shared: state.shared,
    })

    reloadTable(state.shared)
};

const operateFormula = async (id, mode) => {
    if (mode == 'edit') {
        router.push({ name: 'EditFormula', params: { id: id, shared: state.shared ?? 'Company' } });
    } else if (mode == 'view') {
        await fetchFormula(id, state.shared);
        viewDetailsDialog.value = true;
    }
};

const confirmDelete = (id) => {
    confirm.require({
        header: 'Delete Confirmation',
        message: 'Are you sure you want to delete formula ' + id + '?',
        icon: 'pi pi-info-circle',
        accept: async () => {
            // reload table
            await deleteFormula(id, state.shared);
            await reloadTable();
        },
        reject: () => {
            toast.add({ severity: 'error', summary: 'Rejected', detail: 'Cancelled', life: 5000 });
        },
    });
};
</script>
